import {
  ApiResponse,
  Appointment,
  CarsCompletedEffortCounts,
  CarsCompletedThroughputData,
  CarsCompletedThroughputRow,
  CarsByEffortData,
  CarsByEffortPeriodRow,
  CarsThroughputPeriodGroupBy,
  CompletedByEffortSummary,
  DailyBalance,
  DashboardProgressStepRow,
  MonthlyBalanceData,
  PendingPaymentsByYearData,
} from "../types";
import { groupDashboardProgressStepsIntoAppointments } from "../utils/appointmentProgressUtils";
import { apiClient } from "./apiClient";

function normalizeEffortKey(key: string): keyof CarsCompletedEffortCounts | null {
  const normalized = key
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  if (normalized === "leve") return "leve";
  if (normalized === "medio") return "medio";
  if (normalized === "pesado") return "pesado";
  return null;
}

function normalizeEffortCounts(
  byEffort: Record<string, number> | CarsCompletedEffortCounts | undefined,
): CarsCompletedEffortCounts {
  const result: CarsCompletedEffortCounts = { leve: 0, medio: 0, pesado: 0 };
  if (!byEffort) return result;

  for (const [key, value] of Object.entries(byEffort)) {
    const normalizedKey = normalizeEffortKey(key);
    if (normalizedKey != null) {
      result[normalizedKey] = value;
    }
  }

  return result;
}

function normalizeThroughputRow(
  row: CarsCompletedThroughputRow & {
    byEffort?: Record<string, number> | CarsCompletedEffortCounts;
    byService?: CarsCompletedThroughputRow["byService"];
  },
): CarsCompletedThroughputRow {
  return {
    ...row,
    byEffort: normalizeEffortCounts(row.byEffort),
    byService: (row.byService ?? []).map((service) => ({
      ...service,
      byEffort: normalizeEffortCounts(service.byEffort),
    })),
  };
}

function normalizeThroughputRows(
  rows: CarsCompletedThroughputRow[] | undefined,
): CarsCompletedThroughputRow[] {
  return (rows ?? []).map(normalizeThroughputRow);
}

function emptyCarsByEffortBuckets(): Record<
  keyof CarsCompletedEffortCounts,
  { cars: CarsByEffortPeriodRow["byEffort"]["leve"]["cars"] }
> {
  return { leve: { cars: [] }, medio: { cars: [] }, pesado: { cars: [] } };
}

function normalizeCarsByEffortPeriodRow(
  row: CarsByEffortPeriodRow & {
    byEffort?: Record<string, { cars?: CarsByEffortPeriodRow["byEffort"]["leve"]["cars"] }>;
  },
): CarsByEffortPeriodRow {
  const buckets = emptyCarsByEffortBuckets();

  if (row.byEffort) {
    for (const [key, bucket] of Object.entries(row.byEffort)) {
      const normalizedKey = normalizeEffortKey(key);
      if (normalizedKey != null) {
        buckets[normalizedKey] = { cars: bucket?.cars ?? [] };
      }
    }
  }

  return { period: row.period, byEffort: buckets };
}

interface DashboardApiResponse {
  message: string;
  data: {
    today: {
      appointmentsCount: number;
      totalRevenue: number;
      pending: number;
      completed: number;
    };
    weekly: {
      totalRevenue: number;
    };
    monthly: {
      totalRevenue: number;
    };
  };
}

interface DashboardData {
  todayAppointments: number;
  totalRevenue: string;
  upcomingAppointments: number;
  completedAppointments: number;
  weeklyRevenue: string;
  monthlyRevenue: string;
}

export const adminApi = {
  // Get dashboard data
  async getDashboardData(): Promise<ApiResponse<DashboardData>> {
    try {
      const response = await apiClient.get<DashboardApiResponse>("/dashboard");

      const { today, weekly, monthly } = response.data;
      // Map API response to expected format
      const mappedData: DashboardData = {
        todayAppointments: today.appointmentsCount,
        totalRevenue: today.totalRevenue.toFixed(2),
        upcomingAppointments: today.pending,
        completedAppointments: today.completed,
        weeklyRevenue: weekly.totalRevenue.toFixed(2),
        monthlyRevenue: monthly.totalRevenue.toFixed(2),
      };

      return {
        success: true,
        data: mappedData,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch dashboard data",
      };
    }
  },

  // Get appointments per day for current month
  async getAppointmentsPerDay(
    year?: number,
    month?: number,
    professionalId?: number
  ): Promise<ApiResponse<Record<string, Record<string, number>>>> {
    try {
      let url = `/dashboard/appointments-per-day`;
      const params = new URLSearchParams();
      if (year) params.append("year", year.toString());
      if (month) params.append("month", month.toString());
      if (professionalId) params.append("professionalId", professionalId.toString());
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await apiClient.get<{
        message: string;
        data: Record<string, Record<string, number>>;
      }>(url);

      return {
        success: true,
        data: response.data, // Extract the actual data object from the response
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch appointments per day",
      };
    }
  },

  // Get last 12 months revenue
  async getLast12MonthsRevenue(professionalId?: number): Promise<ApiResponse<{ month: string; revenue: number }[]>> {
    try {
      let url = `/dashboard/revenue/last-12-months`;
      if (professionalId) {
        url += `?professionalId=${professionalId}`;
      }

      const response = await apiClient.get<{
        message: string;
        data: { month: string; revenue: number }[];
      }>(url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch last 12 months revenue",
      };
    }
  },


  // Get next 12 months revenue
  async getNext12MonthsRevenue(professionalId?: number): Promise<ApiResponse<{ month: string; revenue: number }[]>> {
    try {
      let url = `/dashboard/revenue/next-12-months`;
      if (professionalId) {
        url += `?professionalId=${professionalId}`;
      }

      const response = await apiClient.get<{
        message: string;
        data: { month: string; revenue: number }[];
      }>(url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch next 12 months revenue",
      };
    }
  },

  // Get last 4 weeks revenue
  async getLast4WeeksRevenue(
    year?: number,
    month?: number,
    professionalId?: number
  ): Promise<
    ApiResponse<
      {
        week: string;
        weekStart: string;
        revenue: number;
        projectedRevenue: number;
      }[]
    >
  > {
    try {
      let url = `/dashboard/revenue/weekly`;
      const params = new URLSearchParams();
      if (year) params.append("year", year.toString());
      if (month) params.append("month", month.toString());
      if (professionalId) params.append("professionalId", professionalId.toString());
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await apiClient.get<{
        message: string;
        data: {
          week: string;
          weekStart: string;
          revenue: number;
          projectedRevenue: number;
        }[];
      }>(url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch weekly revenue",
      };
    }
  },

  // Get monthly balance (totals + byPaymentMethod + appointmentsWithoutPayment)
  async getMonthlyBalance(
    month: number,
    year: number,
    professionalId?: number
  ): Promise<ApiResponse<MonthlyBalanceData>> {
    try {
      const params = new URLSearchParams({
        month: month.toString(),
        year: year.toString(),
      });
      if (professionalId != null) {
        params.append("professionalId", professionalId.toString());
      }
      const url = `/dashboard/balance/monthly?${params.toString()}`;

      const response = await apiClient.get<
        | { message: string; data: MonthlyBalanceData }
        | (MonthlyBalanceData & { message?: string })
      >(url);

      const raw = response as { message?: string; data?: MonthlyBalanceData } & Partial<MonthlyBalanceData>;
      const payload: MonthlyBalanceData =
        raw.data != null
          ? raw.data
          : {
              total: raw.total ?? 0,
              totalWithoutPayment: raw.totalWithoutPayment,
              byPaymentMethod: raw.byPaymentMethod ?? {
                PIX: 0,
                Débito: 0,
                Crédito: 0,
                Dinheiro: 0,
              },
              appointmentsWithoutPayment: raw.appointmentsWithoutPayment ?? [],
              byService: raw.byService ?? [],
            };

      return {
        success: true,
        data: {
          ...payload,
          appointmentsWithoutPayment: payload.appointmentsWithoutPayment ?? [],
        },
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch monthly balance",
      };
    }
  },

  async getPendingPaymentsByYear(
    year: number
  ): Promise<ApiResponse<PendingPaymentsByYearData>> {
    try {
      const url = `/dashboard/balance/pending-payments?year=${year}`;
      const response = await apiClient.get<
        | { message: string; data: PendingPaymentsByYearData }
        | (PendingPaymentsByYearData & { message?: string })
      >(url);

      const raw = response as {
        message?: string;
        data?: PendingPaymentsByYearData;
      } & Partial<PendingPaymentsByYearData>;
      const payload: PendingPaymentsByYearData =
        raw.data != null
          ? raw.data
          : {
              year: raw.year ?? year,
              total: raw.total ?? 0,
              count: raw.count ?? 0,
              clients: raw.clients ?? [],
            };

      return {
        success: true,
        data: {
          ...payload,
          clients: payload.clients ?? [],
        },
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch pending payments",
      };
    }
  },

  /** Completed appointments with car, counts and average duration by effort (date range). */
  async getCompletedByEffort(params: {
    startDate: string;
    endDate: string;
    professionalId?: number;
  }): Promise<ApiResponse<CompletedByEffortSummary>> {
    try {
      const q = new URLSearchParams({
        startDate: params.startDate,
        endDate: params.endDate,
      });
      if (params.professionalId != null) {
        q.append("professionalId", params.professionalId.toString());
      }

      const response = await apiClient.get<
        CompletedByEffortSummary | { message?: string; data: CompletedByEffortSummary }
      >(`/dashboard/completed-by-effort?${q.toString()}`);

      const raw = response as CompletedByEffortSummary & { data?: CompletedByEffortSummary };
      const payload: CompletedByEffortSummary | undefined =
        raw && typeof raw === "object" && Array.isArray(raw.byEffort)
          ? (raw as CompletedByEffortSummary)
          : raw.data;

      if (!payload || typeof payload !== "object") {
        return {
          success: false,
          error: "Resposta inválida do servidor (completed-by-effort)",
        };
      }

      return {
        success: true,
        data: {
          ...payload,
          byEffort: payload.byEffort ?? [],
        },
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to fetch completed-by-effort summary",
      };
    }
  },

  /** Cars completed throughput by day/week/month (Relatórios → Produção). */
  async getCarsCompletedThroughput(params: {
    startDate: string;
    endDate: string;
    professionalId?: number;
  }): Promise<ApiResponse<CarsCompletedThroughputData>> {
    try {
      const q = new URLSearchParams({
        startDate: params.startDate,
        endDate: params.endDate,
      });
      if (params.professionalId != null) {
        q.set("professionalId", params.professionalId.toString());
      }

      const response = await apiClient.get<
        CarsCompletedThroughputData | { message?: string; data: CarsCompletedThroughputData }
      >(`/dashboard/cars-completed-throughput?${q.toString()}`);

      const raw = response as CarsCompletedThroughputData & {
        data?: CarsCompletedThroughputData;
      };
      const payload: CarsCompletedThroughputData | undefined =
        raw && typeof raw === "object" && Array.isArray(raw.byDay)
          ? (raw as CarsCompletedThroughputData)
          : raw.data;

      if (!payload || typeof payload !== "object") {
        return {
          success: false,
          error: "Resposta inválida do servidor (cars-completed-throughput)",
        };
      }

      return {
        success: true,
        data: {
          ...payload,
          byDay: normalizeThroughputRows(payload.byDay),
          byWeek: normalizeThroughputRows(payload.byWeek),
          byMonth: normalizeThroughputRows(payload.byMonth),
          progressSpan: payload.progressSpan ?? {
            overallAverageMinutes: 0,
            overallEstimatedCarsPer8HourDay: 0,
            byEffort: [],
            byService: [],
          },
        },
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch cars completed throughput",
      };
    }
  },

  /** Completed cars by effort for a period (drill-down from cars-throughput). */
  async getCarsByEffort(params: {
    startDate: string;
    endDate: string;
    groupBy: CarsThroughputPeriodGroupBy;
    professionalId?: number;
  }): Promise<ApiResponse<CarsByEffortData>> {
    try {
      const q = new URLSearchParams({
        startDate: params.startDate,
        endDate: params.endDate,
        groupBy: params.groupBy,
      });
      if (params.professionalId != null) {
        q.set("professionalId", params.professionalId.toString());
      }

      const response = await apiClient.get<
        CarsByEffortData | { message?: string; data: CarsByEffortData }
      >(`/dashboard/cars-by-effort?${q.toString()}`);

      const raw = response as CarsByEffortData & { data?: CarsByEffortData };
      const payload: CarsByEffortData | undefined =
        raw && typeof raw === "object" && Array.isArray(raw.periods)
          ? raw
          : raw.data;

      if (!payload || typeof payload !== "object") {
        return {
          success: false,
          error: "Resposta inválida do servidor (cars-by-effort)",
        };
      }

      return {
        success: true,
        data: {
          ...payload,
          groupBy: payload.groupBy ?? params.groupBy,
          periods: (payload.periods ?? []).map(normalizeCarsByEffortPeriodRow),
        },
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch cars by effort",
      };
    }
  },

  /** Progress steps for a professional and date range (Relatórios → Funcionário). */
  async getDashboardProgressSteps(params: {
    professionalId: number;
    startDate: string;
    endDate: string;
  }): Promise<ApiResponse<Appointment[]>> {
    try {
      const q = new URLSearchParams({
        professionalId: params.professionalId.toString(),
        startDate: params.startDate,
        endDate: params.endDate,
      });
      const response = await apiClient.get<{
        message?: string;
        data: DashboardProgressStepRow[];
      }>(`/dashboard/progress-steps?${q.toString()}`);

      const rows = Array.isArray(response.data) ? response.data : [];

      return {
        success: true,
        data: groupDashboardProgressStepsIntoAppointments(rows),
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to fetch dashboard progress steps",
      };
    }
  },

  // Get daily balance
  async getDailyBalance(date: string, professionalId?: number): Promise<ApiResponse<DailyBalance>> {
    try {
      let url = `/dashboard/balance/daily?date=${date}`;
      if (professionalId) {
        url += `&professionalId=${professionalId}`;
      }

      const response = await apiClient.get<{
        message: string;
        data: DailyBalance;
      }>(url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch daily balance",
      };
    }
  },
};
